import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  apiClient,
  cacheAuthoritativeSession,
  clearCachedSession,
} from "../pages/backend_url";
import "./ProtectedRoute.css";

export default function ProtectedRoute({ children, onAuthenticated }) {
  const [checkNumber, setCheckNumber] = useState(0);
  const [status, setStatus] = useState("checking");

  const retry = useCallback(() => {
    setStatus("checking");
    setCheckNumber((value) => value + 1);
  }, []);

  useEffect(() => {
    if (status !== "error") return undefined;
    const timer = window.setTimeout(retry, 5000);
    window.addEventListener("online", retry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", retry);
    };
  }, [retry, status]);

  useEffect(() => {
    const abortController = new AbortController();
    let active = true;

    const bootstrapSession = async () => {
      try {
        const response = await apiClient.get("/auth/session", {
          headers: { "Cache-Control": "no-store" },
          signal: abortController.signal,
        });
        const cachedUser = cacheAuthoritativeSession(response.data);

        if (active) {
          onAuthenticated?.(cachedUser);
          setStatus("authenticated");
        }
      } catch (error) {
        if (!active || error?.code === "ERR_CANCELED") return;

        if (error?.response?.status === 401) {
          clearCachedSession();
          setStatus("unauthenticated");
          return;
        }

        // A network or server failure must not turn a browser cache into
        // authorization. Keep the route closed and let the user retry.
        setStatus("error");
      }
    };

    bootstrapSession();

    return () => {
      active = false;
      abortController.abort();
    };
  }, [checkNumber, onAuthenticated]);

  if (status === "unauthenticated") {
    return <Navigate to="/" replace />;
  }

  if (status === "error") {
    return (
      <main className="protected-route-state" role="alert" aria-live="assertive">
        <div className="protected-route-card">
          <h1>CRM service is temporarily unavailable</h1>
          <p>Protected data remains secure. This page will reconnect automatically when the service is ready.</p>
          <button type="button" onClick={retry}>
            Retry now
          </button>
        </div>
      </main>
    );
  }

  if (status !== "authenticated") {
    return (
      <main
        className="protected-route-state"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="protected-route-card protected-route-loading">
          <span className="protected-route-spinner" aria-hidden="true" />
          <div>
            <h1>Verifying your session</h1>
            <p>Securely loading your CRM account...</p>
          </div>
        </div>
      </main>
    );
  }

  return children;
}
